import {
  IContact,
  IUserProperty,
  IUserSuite,
} from 'src/services/api/ServiceRequestDashboard/apiContracts';
import {
  IContactAdvancedFilters,
  IndividualFilter,
} from 'src/pages/Contacts/CustomHooks/useContactAdvancedFilters';
import Fuse, { FuseResult } from 'fuse.js';

const filterByRoles = (
  contacts: IContact[],
  filters: IContactAdvancedFilters
): IContact[] => {
  if (!filters.roles || filters.roles.length === 0) {
    return contacts;
  }

  return contacts.filter((contact: IContact): boolean => {
    // For each contact, check if any of its roles match the selected roles.
    for (const roleWrapper of contact.roles) {
      const contactRoleName: string = roleWrapper.role.name;
      const isRoleSelected: boolean = filters.roles?.includes(contactRoleName);

      // If this role matches any selected role, include this contact.
      if (isRoleSelected) {
        return true;
      }
    }

    // If we've checked all roles and found no match, exclude this contact.
    return false;
  });
};

const filterByProperties = (
  contacts: IContact[],
  filters: IContactAdvancedFilters
): IContact[] => {
  if (!filters.property || filters.property.length === 0) {
    return contacts;
  }

  const result: IContact[] = contacts.filter((contact: IContact) => {
    return filters.property.some((filterProperty: any) => {
      if (!contact.properties) {
        return false;
      }

      const propertyIdInContactProperties = contact.properties.some(
        (singleProperty: IUserProperty) => {
          return singleProperty.propertyId === filterProperty.Id;
        }
      );

      return propertyIdInContactProperties;
    });
  });

  return result;
};

const filterByStatus = (
  contacts: IContact[],
  filters: IContactAdvancedFilters
): IContact[] => {
  if (!filters.status || filters.status.length === 0) {
    return contacts;
  }

  return contacts.filter((contact: IContact): boolean => {
    const contactStatus: string = contact.status;
    const isStatusSelected: boolean = filters.status?.includes(contactStatus);

    return isStatusSelected;
  });
};

const filterByEmail = (
  contacts: IContact[],
  filters: IContactAdvancedFilters
): IContact[] => {
  if (!filters.email || filters.email.trim() === '') {
    return contacts;
  }

  const validContacts: IContact[] = contacts.filter(
    (contact: IContact) => contact.email && contact.email.trim() !== ''
  );

  const emailSearch: string = filters.email.toLowerCase().trim();

  // First try exact substring matching.
  const exactMatches: IContact[] = validContacts.filter(
    (contact: IContact): boolean => {
      return contact.email.toLowerCase().includes(emailSearch);
    }
  );

  // If exact matching gives good results, use those.
  if (exactMatches.length > 0) {
    return exactMatches;
  }

  // Otherwise, fall back to fuzzy search for typos.
  const fuseOptions = {
    threshold: 0.3,
    keys: ['email'],
    ignoreLocation: true,
  };

  const fuse = new Fuse(contacts, fuseOptions);
  const searchResults = fuse.search(emailSearch);
  return searchResults.map((result: FuseResult<IContact>) => result.item);
};

const filterByPhone = (
  contacts: IContact[],
  filters: IContactAdvancedFilters
): IContact[] => {
  if (!filters.phone || filters.phone.trim() === '') {
    return contacts;
  }

  // Remove non-digits from search term for consistent comparison
  const phoneSearch: string = filters.phone.trim().replace(/\D/g, '');

  // If the search is empty after cleaning, return all contacts
  if (phoneSearch === '') {
    return contacts;
  }

  return contacts.filter((contact: IContact): boolean => {
    // Remove non-digits from contact's phone for consistent comparison
    const contactPhone: string = contact.phone.replace(/\D/g, '');
    return contactPhone.includes(phoneSearch);
  });
};

const filterByDateRange = (
  contacts: IContact[],
  filters: IContactAdvancedFilters
): IContact[] => {
  if (
    !filters.dateRange ||
    (!filters.dateRange.startDate && !filters.dateRange.endDate)
  ) {
    return contacts;
  }

  return contacts.filter((contact: IContact): boolean => {
    // If contact has no invitedOn date, it can't match the date filter
    if (!contact.invitedOn) {
      return false;
    }

    const invitedDate = new Date(contact.invitedOn);

    const hasStartDateFilter: boolean = !!filters.dateRange?.startDate;

    if (hasStartDateFilter) {
      const startDate: Date = filters.dateRange!.startDate!;
      const isAfterStartDate: boolean = invitedDate >= startDate;

      if (!isAfterStartDate) {
        return false; // Date is before our filter range.
      }
    }

    const hasEndDateFilter: boolean = !!filters.dateRange?.endDate;

    if (hasEndDateFilter) {
      const endDate = new Date(filters.dateRange!.endDate!);
      endDate.setDate(endDate.getDate() + 1);

      const isBeforeEndDate: boolean = invitedDate < endDate;

      if (!isBeforeEndDate) {
        return false; // Date is after our filter range.
      }
    }

    // If we passed both start and end date checks, include this contact.
    return true;
  });
};

const filterByName = (
  contacts: IContact[],
  filters: IContactAdvancedFilters
): IContact[] => {
  if (!filters.name || filters.name.trim() === '') {
    return contacts;
  }

  const searchTerm: string = filters.name.toLowerCase().trim();

  return contacts.filter((contact: IContact): boolean => {
    const firstNameMatches: boolean =
      contact.firstname?.toLowerCase().includes(searchTerm) || false;
    const lastNameMatches: boolean =
      contact.lastname?.toLowerCase().includes(searchTerm) || false;

    const fullName =
      `${contact.firstname || ''} ${contact.lastname || ''}`.toLowerCase();
    const fullNameMatches: boolean = fullName.includes(searchTerm);

    // Return true if any name field matches
    return firstNameMatches || lastNameMatches || fullNameMatches;
  });
};

const filterByUserSuiteField = (
  contacts: IContact[],
  selectedFilter: IndividualFilter[],
  suiteFieldName: keyof IUserSuite
): IContact[] => {
  if (!selectedFilter || selectedFilter.length === 0) {
    return contacts;
  }

  return contacts.filter((contact: IContact) => {
    if (!Array.isArray(contact.userSuites)) {
      return false;
    }

    const selectedValue: string = selectedFilter[0]?.name;

    if (!selectedValue) {
      return false;
    }

    const matches: boolean = contact.userSuites.some((userSuite) => {
      return userSuite[suiteFieldName] === selectedValue;
    });

    return matches;
  });
};

const applyAllFilters = (
  contacts: IContact[],
  filters: IContactAdvancedFilters
): IContact[] => {
  let result: IContact[] = [...contacts];

  result = filterByRoles(result, filters);
  result = filterByProperties(result, filters);
  result = filterByStatus(result, filters);
  result = filterByEmail(result, filters);
  result = filterByPhone(result, filters);
  result = filterByName(result, filters);
  result = filterByDateRange(result, filters);
  result = filterByUserSuiteField(
    result,
    filters.selectedBuilding,
    'buildingName'
  );
  result = filterByUserSuiteField(result, filters.selectedLevel, 'levelName');
  result = filterByUserSuiteField(result, filters.selectedUnit, 'suiteName');

  return result;
};

const normalizeUserSuites = (contacts: IContact[]): IContact[] => {
  return contacts.map((contact: IContact) => ({
    ...contact,
    userSuites: contact.userSuites === null ? null : contact.userSuites,
  }));
};

// TODO: Finish implementing this logic from https://app.asana.com/1/41213529744312/project/1208712340140532/task/1210618856822411?focus=true
const normalizeUserSuiteFields = (contacts: IContact[]): IContact[] => {
  return contacts.map((contact: IContact) => {
    if (!Array.isArray(contact.userSuites)) {
      return contact;
    }

    const normalizedUserSuites = contact.userSuites.map((suite) => ({
      ...suite,
      propertyName: suite.propertyName === '' ? 'N/A' : suite.propertyName,
      suiteName: suite.suiteName === '' ? 'N/A' : suite.suiteName,
      buildingName: suite.buildingName === '' ? 'N/A' : suite.buildingName,
      levelName: suite.levelName === '' ? 'N/A' : suite.levelName,
    }));

    return {
      ...contact,
      userSuites: normalizedUserSuites,
    };
  });
};

// TODO: Finish implementing this logic from https://app.asana.com/1/41213529744312/project/1208712340140532/task/1210618856822411?focus=true
const removeFilterFromActiveFilters = (
  currentFilters: IContactAdvancedFilters,
  filterToRemove: string
): IContactAdvancedFilters => {
  const updatedFilters: Partial<IContactAdvancedFilters> = {};

  (Object.keys(currentFilters) as Array<keyof IContactAdvancedFilters>).forEach(
    (key) => {
      const value = currentFilters[key];

      // Guard clause: Skip undefined/null values.
      if (value === undefined || value === null) {
        return;
      }

      // Handle array filters (roles, properties, status).
      if (Array.isArray(value)) {
        let filteredArray;

        if (key === 'property') {
          filteredArray = value.filter(
            (item: any) => item.name !== filterToRemove
          );
        } else {
          filteredArray = value.filter((item: any) => item !== filterToRemove);
        }

        // Only add if array still has items
        if (filteredArray.length > 0) {
          (updatedFilters as any)[key] = filteredArray;
        }

        return;
      }

      // Handle string filters (email, phone, name).
      if (typeof value === 'string') {
        if (value !== filterToRemove) {
          (updatedFilters as any)[key] = value;
        }

        return; // Exclude the current filter from the returned 'updatedFilters' result set.
      }

      // Handle object filters (dateRange) - preserve as-is.
      (updatedFilters as any)[key] = value;
    }
  );

  return updatedFilters as IContactAdvancedFilters;
};

/* Recursively checks if our filters contain any meaningful/truthy content
 * Uses depth-first search to explore nested objects and arrays
 * TODO: Finish implementing this logic from https://app.asana.com/1/41213529744312/project/1208712340140532/task/1210618856822411?focus=true*/
const filtersHasActiveTruthyValue = (contactFilters: any): boolean => {
  if (contactFilters == null) {
    return false;
  }

  // Handle arrays - check if any element is truthy.
  if (Array.isArray(contactFilters)) {
    return (
      contactFilters.length > 0 &&
      contactFilters.some((item) => filtersHasActiveTruthyValue(item))
    );
  }

  // Handle objects - recursively check all properties.
  if (typeof contactFilters === 'object') {
    return Object.values(contactFilters).some((prop) =>
      filtersHasActiveTruthyValue(prop)
    );
  }

  // Handle primitives (string, number, boolean)
  return !!contactFilters;
};

/* Checks if the filters object contains any active filters - Uses a recursive DFS to handle any nested structure -
 * TODO: Finish implementing this logic from https://app.asana.com/1/41213529744312/project/1208712340140532/task/1210618856822411?focus=true */
const checkIfFiltersAreActive = (filters: IContactAdvancedFilters): boolean => {
  if (Object.keys(filters).length === 0) {
    return false;
  }

  return filtersHasActiveTruthyValue(filters);
};

export {
  applyAllFilters,
  filterByRoles,
  filterByProperties,
  filterByStatus,
  filterByEmail,
  filterByPhone,
  filterByDateRange,
  normalizeUserSuiteFields,
  normalizeUserSuites,
  removeFilterFromActiveFilters,
  checkIfFiltersAreActive,
  filterByUserSuiteField,
};
